import { PENDING_SLOT } from "@/components/editor/refPicker/refPickerModel";
import type { ReferenceForm } from "@/lib/tiptap/extensions/reference";

/**
 * What an inline reference shows in the editor — SPEC-references §3.
 *
 * The rule is that the editor prints what the PDF will print. Every string
 * below was measured, not assumed: a document with each command pointing at a
 * table, an equation, a section, a figure and a theorem, compiled with
 * pdflatex through the API and read back with pdftotext (25 Sep).
 *
 *   \ref      1                \eqref   (1)          \pageref  1
 *   \cref     table 1 · eq. (1) · section 1 · fig. 1 · theorem 1
 *   \Cref     Table 1 · Equation (1) · Section 1 · Figure 1 · Theorem 1
 *   \autoref  Table 1 · Equation 1 · section 1 · Figure 1 · Theorem 1
 *   \nameref  the caption or title
 *   dangling  ??  (and (??) for \eqref)
 *
 * Algorithms and listings were not measured — a listing's label does not reach
 * the PDF today — so for those kinds the words are left out and the number
 * shows alone, rather than a word that might not match the PDF.
 *
 * Where the number is not known yet, the slot ▯ stands in the number's place,
 * never a question mark: ?? is reserved for a reference that is broken, so the
 * editor and the PDF agree on what "broken" looks like.
 */

export type InlineRefState = "numbered" | "pending" | "dangling" | "unresolved";

export interface InlineTarget {
  kind: string;
  number: string | null;
  page: number | null;
  caption: string | null;
}

export interface InlineReference {
  text: string;
  state: InlineRefState;
  title: string;
}

/** Two word sets per kind: cleveref's (\cref, \Cref) and hyperref's (\autoref). */
const CREF: Record<string, { lower: string; upper: string; parens?: boolean }> = {
  table: { lower: "table", upper: "Table" },
  equation: { lower: "eq.", upper: "Equation", parens: true },
  section: { lower: "section", upper: "Section" },
  figure: { lower: "fig.", upper: "Figure" },
  theorem: { lower: "theorem", upper: "Theorem" },
};

const AUTOREF: Record<string, string> = {
  table: "Table",
  equation: "Equation",
  section: "section",
  figure: "Figure",
  theorem: "Theorem",
};

/** Plain "compile": the quick preview numbers references too, now (Olivia's rule). */
export const PENDING_TITLE = "No number yet — compile to resolve";

export function inlineReference(
  form: ReferenceForm,
  key: string,
  /** The document's index; null until it has loaded. */
  index: ReadonlyMap<string, InlineTarget> | null,
): InlineReference {
  if (!index) return { text: keyText(key), state: "unresolved", title: `Reference: ${key}` };

  const target = index.get(key);
  if (!target) {
    return {
      text: form === "eqref" ? "(??)" : "??",
      state: "dangling",
      title: `Nothing is labelled ${key} — the PDF will print ??`,
    };
  }

  const pending = target.number === null;
  const n = target.number ?? PENDING_SLOT;
  const state: InlineRefState = pending ? "pending" : "numbered";
  const title = pending ? PENDING_TITLE : key;

  switch (form) {
    case "eqref":
      return { text: `(${n})`, state, title };
    case "pageref":
      return target.page !== null
        ? { text: String(target.page), state: "numbered", title: key }
        : { text: PENDING_SLOT, state: "pending", title: PENDING_TITLE };
    case "nameref":
      // The caption is known without compiling, so this one is never pending.
      return target.caption
        ? { text: target.caption, state: "numbered", title: key }
        : { text: keyText(key), state: "unresolved", title: `Reference: ${key}` };
    case "cref":
    case "Cref": {
      const words = CREF[target.kind];
      if (!words) return { text: n, state, title };
      const num = words.parens ? `(${n})` : n;
      return { text: `${form === "Cref" ? words.upper : words.lower} ${num}`, state, title };
    }
    case "autoref": {
      const word = AUTOREF[target.kind];
      return { text: word ? `${word} ${n}` : n, state, title };
    }
    default:
      return { text: n, state, title };
  }
}

/**
 * Before the index arrives: the label, made readable. What the editor showed
 * for every reference until now, kept for the moment before the first fetch.
 */
export function keyText(key: string): string {
  const colon = key.indexOf(":");
  return colon > 0 ? key.slice(colon + 1) : key;
}
